import React, { useState, useEffect, useMemo, useRef } from "react";
import { BigFormModal } from "@/components/ui/big-form-modal";
import { AgeGroupVariationForm } from "./AgeGroupVariationForm";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { CampLocation } from "@/types/campLocation";
import { db } from "@/services/firebaseService";
import { FieldStateProvider, encodeFieldPath } from "@/services/campDataFieldState";
import { FieldOwnershipBadge } from "@/components/common/FieldOwnershipBadge";
import { useAuth } from "@/context/AuthContext";
import { resolveAudience } from "@/services/campDataAudience";
import { updateCampLocation } from "@/services/campLocationUpdateService";
import { AgeGroupVariationFormData } from "./schemas/ageGroupVariationSchema";
import { locationProgramVariationSchema } from "./schemas/locationProgramVariationSchema";
import { useDecimalEnums } from "@/components/admin/locations/hooks/useDecimalEnums";
import { keepFieldsTheFormDoesNotOwn, unappliedJsonVariationKeys } from "@/components/admin/locations/utils/variationEditMerge";
import { sessionWindowFill } from "@/functions-shared/offer/sessionWindowRule";

export type VariationFormData = AgeGroupVariationFormData;
interface AgeGroupVariationModalProps {
  isOpen: boolean;
  onClose: () => void;
  location: CampLocation;
  variationToEdit?: VariationFormData | null;
  onSaved: (updatedLocation: CampLocation) => void;
  isWelcomeScreen?: boolean;
  isCopyMode?: boolean;
  savedFormDataForAddAnother?: VariationFormData | null;
}
export const AgeGroupVariationModal: React.FC<AgeGroupVariationModalProps> = ({
  isOpen,
  onClose,
  location,
  variationToEdit,
  onSaved,
  isWelcomeScreen = false,
  isCopyMode = false,
  savedFormDataForAddAnother = null
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [mode, setMode] = useState<"form" | "json">("form");
  const [jsonText, setJsonText] = useState("");
  const [formSnapshot, setFormSnapshot] = useState<VariationFormData | null>(null);
  const [formRemountKey, setFormRemountKey] = useState(0);
  const prevIsOpenRef = useRef(false);
  const { currentUser, userProfile, userRoles, isAdmin: authIsAdmin } = useAuth();
  const isAdmin = userProfile?.role === "admin_user";
  // KB-3318: this modal mounts its own FieldStateProvider (a portal), so it resolves its own audience
  // rather than inheriting the page's — director in the welcome flow, staff for an admin. A guest with
  // no resolved role (no invitation reaches this modal) → null → no badge, never a false staff control.
  const fieldAudience = resolveAudience({ userProfile, userRoles, isAdmin: authIsAdmin });
  const { enumsMap } = useDecimalEnums();
  
  // Add debugging when modal opens for editing
  React.useEffect(() => {
    if (isOpen && variationToEdit && !isCopyMode) {
      console.log("[AgeGroupVariationModal] Opening for EDIT with variationToEdit:", variationToEdit);
      console.log("[AgeGroupVariationModal] Additional fields received for editing:");
      console.log("  - description:", variationToEdit.description);
      console.log("  - url:", variationToEdit.url);
      console.log("  - dataFetchUrl:", variationToEdit.dataFetchUrl);
      console.log("  - defaultSessionUrl:", variationToEdit.defaultSessionUrl);
      console.log("  - defaultSessionDescription:", variationToEdit.defaultSessionDescription);
    }
  }, [isOpen, variationToEdit, isCopyMode]);
  
  // Add another checkbox state
  const [addAnotherChecked, setAddAnotherChecked] = useState(false);
  const [savedFormData, setSavedFormData] = useState<VariationFormData | null>(null);
  
  const isEditMode = !!variationToEdit && !isCopyMode;
  const modalTitle = isEditMode ? "Edit program" : isCopyMode ? "Copy program" : "Add program";
  
  // Initialize checkbox state - if we have savedFormDataForAddAnother, we're in "Add another" mode
  useEffect(() => {
    if (isOpen && savedFormDataForAddAnother) {
      console.log("[AgeGroupVariationModal] In 'Add another' mode, setting checkbox to checked");
      setAddAnotherChecked(true);
    }
  }, [isOpen, savedFormDataForAddAnother]);
  
  // Extract existing keys from location.variations
  const existingKeys = useMemo(() => {
    if (!location.variations) return [];
    
    // If in edit mode, exclude the current key to avoid self-comparison
    if (isEditMode && variationToEdit) {
      return location.variations
        .filter(variation => variation.key !== variationToEdit.key)
        .map(variation => variation.key);
    }
    
    // Otherwise return all keys
    return location.variations.map(variation => variation.key);
  }, [location.variations, isEditMode, variationToEdit]);
  
  // Helper function to calculate derived timing fields
  const calculateDerivedTimings = (formData: VariationFormData): VariationFormData => {
    const updatedData = {
      ...formData
    };
    const timings = updatedData.timings || {};

    // Calculate dropOffEnd (set within timings)
    if (timings.regularDropOffEnd !== undefined && timings.regularDropOffEnd !== null) {
      timings.dropOffEnd = timings.regularDropOffEnd;
    }

    // Calculate dropOffStart - lesser of regularDropOffStart and beforeCareStart (set within timings)
    if (timings.regularDropOffStart !== undefined && timings.regularDropOffStart !== null) {
      let dropOffStart = timings.regularDropOffStart;
      if (timings.beforeCareStart !== undefined && timings.beforeCareStart !== null) {
        dropOffStart = Math.min(dropOffStart, timings.beforeCareStart);
      }
      timings.dropOffStart = dropOffStart;
    }

    // Calculate pickUpStart (set within timings)
    if (timings.regularPickUpStart !== undefined && timings.regularPickUpStart !== null) {
      timings.pickUpStart = timings.regularPickUpStart;
    }

    // Calculate pickUpEnd - greater of regularPickUpEnd and afterCareEnd (set within timings)
    if (timings.regularPickUpEnd !== undefined && timings.regularPickUpEnd !== null) {
      let pickUpEnd = timings.regularPickUpEnd;
      if (timings.afterCareEnd !== undefined && timings.afterCareEnd !== null) {
        pickUpEnd = Math.max(pickUpEnd, timings.afterCareEnd);
      }
      timings.pickUpEnd = pickUpEnd;
    }
    // KB-4541 (N10) — the default drop-off/pick-up window (KB-3613), the one rule every variation writer applies
    // (KB-4516): fill-only, per group, from the programming bounds; a group with any key set is left as it is.
    updatedData.timings = { ...timings, ...sessionWindowFill(timings) };
    return updatedData;
  };
  
  const handleSave = async (formData: VariationFormData) => {
    if (!currentUser) {
      toast.error("You must be logged in to save changes");
      return;
    }
    if (typeof formData?.key === "string" && formData.key.startsWith("g-")) {
      toast.error("Internal ID cannot start with 'g-'. That prefix is reserved for shared programs.");
      return;
    }
    try {
      console.log("[AgeGroupVariationModal] Attempting to save program data:", formData);
      console.log("[AgeGroupVariationModal] Current location data:", location);
      console.log("[AgeGroupVariationModal] Latest version ID:", location.latestVersionId);
      console.log("[AgeGroupVariationModal] Is welcome screen:", isWelcomeScreen);
      console.log("[AgeGroupVariationModal] Is copy mode:", isCopyMode);
      setIsSaving(true);

      console.log("[AgeGroupVariationModal] Processing form data before save...");
      console.log("[AgeGroupVariationModal] Raw form data:", formData);
      
      // Log all the additional fields specifically
      console.log("[AgeGroupVariationModal] Additional fields in form data:");
      console.log("  - description:", formData.description);
      console.log("  - url:", formData.url);
      console.log("  - dataFetchUrl:", formData.dataFetchUrl);
      console.log("  - defaultSessionUrl:", formData.defaultSessionUrl);
      console.log("  - defaultSessionDescription:", formData.defaultSessionDescription);

      // Create the clean data
      const cleanDataForFirestore = (data: VariationFormData): VariationFormData => {
        const result = { ...data };
        
        // Remove colorScheme if it exists (UI-only field)
        if ('colorScheme' in result) {
          delete (result as any).colorScheme;
        }
        
        // Ensure all undefined values become null for Firestore compatibility
        Object.keys(result).forEach(key => {
          if ((result as any)[key] === undefined) {
            (result as any)[key] = null;
          }
        });
        
        return result;
      };

      const finalProcessedData = cleanDataForFirestore(formData);
      
      console.log("[AgeGroupVariationModal] Final processed data for save:");
      console.log("  - Full object:", finalProcessedData);
      console.log("  - Additional fields after processing:");
      console.log("    - description:", finalProcessedData.description);
      console.log("    - url:", finalProcessedData.url);
      console.log("    - dataFetchUrl:", finalProcessedData.dataFetchUrl);
      console.log("    - defaultSessionUrl:", finalProcessedData.defaultSessionUrl);
      console.log("    - defaultSessionDescription:", finalProcessedData.defaultSessionDescription);

      // Calculate derived timing fields within the timings object
      const dataWithDerivedTimings = calculateDerivedTimings(finalProcessedData);
      console.log("[AgeGroupVariationModal] Program data with derived timings:", dataWithDerivedTimings);

      // SIMPLIFIED PROCESSING: Store exactly what user enters/selects
      const processedData = { ...dataWithDerivedTimings };
      
      // Requirements 1-2: Store exactly what user enters for age fields (or null if empty)
      // processedData.minAgeInYears and maxAgeInYears already contain exactly what user entered
      console.log("[AgeGroupVariationModal] Age values from user input:", {
        minAgeInYears: processedData.minAgeInYears,
        maxAgeInYears: processedData.maxAgeInYears
      });

      // Requirements 3-4: Store exactly what user selects for grade fields (or null if empty)
      // Handle "none" selection by converting to null
      if (processedData.minAgeGradeKey === "none" || processedData.minAgeGradeKey === "") {
        processedData.minAgeGradeKey = null;
      }
      if (processedData.maxAgeGradeKey === "none" || processedData.maxAgeGradeKey === "") {
        processedData.maxAgeGradeKey = null;
      }
      
      // Requirements 5-6: Store decimal value from DecimalEnum for selected grades
      if (processedData.minAgeGradeKey && enumsMap.has(processedData.minAgeGradeKey)) {
        const gradeEnum = enumsMap.get(processedData.minAgeGradeKey);
        processedData.minAgeGradeValue = gradeEnum?.value !== undefined ? gradeEnum.value : null;
        console.log(`[AgeGroupVariationModal] Storing minAgeGradeValue: ${processedData.minAgeGradeValue} for grade ${processedData.minAgeGradeKey}`);
      } else {
        processedData.minAgeGradeValue = null;
      }
      
      if (processedData.maxAgeGradeKey && enumsMap.has(processedData.maxAgeGradeKey)) {
        const gradeEnum = enumsMap.get(processedData.maxAgeGradeKey);
        processedData.maxAgeGradeValue = gradeEnum?.value !== undefined ? gradeEnum.value : null;
        console.log(`[AgeGroupVariationModal] Storing maxAgeGradeValue: ${processedData.maxAgeGradeValue} for grade ${processedData.maxAgeGradeKey}`);
      } else {
        processedData.maxAgeGradeValue = null;
      }

      // Requirement 7: Keep showMinAgeGradeAsPrimary logic exactly as-is (no changes needed)
      console.log("[AgeGroupVariationModal] Display preferences:", {
        showMinAgeGradeAsPrimary: processedData.showMinAgeGradeAsPrimary,
        showMaxAgeGradeAsPrimary: processedData.showMaxAgeGradeAsPrimary
      });

      // Make sure visibility is set
      if (!processedData.visibility) {
        processedData.visibility = "v_published";
      }

      // Copy regularPrice to priceRegular for backward compatibility if needed
      if (processedData.regularPrice !== undefined && processedData.regularPrice !== null) {
        processedData.priceRegular = processedData.regularPrice;
      }
      
      // IMPORTANT: Calculate and set numDays based on regularOfferIncrementKey
      if (processedData.regularOfferIncrementKey && 
          enumsMap.has(processedData.regularOfferIncrementKey)) {
        const decimalEnum = enumsMap.get(processedData.regularOfferIncrementKey);
        if (decimalEnum && decimalEnum.value !== undefined && decimalEnum.value !== null) {
          console.log(`[AgeGroupVariationModal] Setting numDays to ${decimalEnum.value} based on regularOfferIncrementKey ${processedData.regularOfferIncrementKey}`);
          processedData.numDays = decimalEnum.value;
        } else {
          console.log(`[AgeGroupVariationModal] No decimal value found for regularOfferIncrementKey ${processedData.regularOfferIncrementKey}`);
        }
      } else {
        console.log(`[AgeGroupVariationModal] No increment key selected or not found in enumsMap`);
      }
      
      const finalProcessedDataForSave = cleanDataForFirestore({
        ...processedData,
        regularOfferIncrementKey: processedData.regularOfferIncrementKey === "oi_none" ? "" : processedData.regularOfferIncrementKey
      });
      
      console.log("[AgeGroupVariationModal] Final processed data for save:", finalProcessedDataForSave);
      
      const variations = Array.isArray(location.variations) ? [...location.variations] : [];

      // Enforce uniqueness of Internal ID (key) within this location when creating/copying
      if (!isEditMode) {
        const duplicate = variations.some(v => v.key === finalProcessedDataForSave.key);
        if (duplicate) {
          console.warn(`[AgeGroupVariationModal] Duplicate program key detected: ${finalProcessedDataForSave.key}`);
          toast.error("Internal ID already in use");
          setIsSaving(false);
          return;
        }
      }
      
      // In add new mode or in copy mode with an empty key, generate a new key
      if (!isEditMode) {
        // Modified behavior: Use the provided key field if not empty (for both copy mode AND new programs)
        if (finalProcessedDataForSave.key && finalProcessedDataForSave.key.trim() !== '') {
          console.log(`[AgeGroupVariationModal] Using user-provided key for ${isCopyMode ? 'copied' : 'new'} program: ${finalProcessedDataForSave.key}`);
          // Keep the user-provided key as-is
        } else {
          // Generate a new key only when no key provided
          finalProcessedDataForSave.key = `var_${Date.now().toString(36)}`;
          console.log(`[AgeGroupVariationModal] Generated new key for ${isCopyMode ? 'copied' : 'new'} program: ${finalProcessedDataForSave.key}`);
        }
        variations.push(finalProcessedDataForSave);
      } else if (isEditMode && variationToEdit) {
        const index = variations.findIndex(v => v.key === variationToEdit.key);
        console.log(`[AgeGroupVariationModal] Editing program at index ${index}, key: ${variationToEdit.key}`);
        if (index !== -1) {
          variations[index] = keepFieldsTheFormDoesNotOwn(variations[index], finalProcessedDataForSave);
        } else {
          console.warn(`[AgeGroupVariationModal] Could not find program with key ${variationToEdit.key}, adding as new`);
          variations.push(finalProcessedDataForSave);
        }
      }
      
      const updatedLocation = {
        ...location,
        variations
      };
      console.log("[AgeGroupVariationModal] Saving updated location with programs:", variations.length);
      console.log("[AgeGroupVariationModal] FORCING UPDATE TO LATEST DRAFT VERSION");

      // Using the correct updateCampLocation from campLocationUpdateService.ts
      // Pass only the variations to the update function, and ALWAYS set forceUpdateLatestDraft to true
      // This ensures we're only updating the latest draft without creating new versions
      console.log("[AgeGroupVariationModal] Calling updateCampLocation with forceUpdateLatestDraft=true");
      await updateCampLocation(db, location.id, {
        variations
      }, currentUser.uid, `${isEditMode ? 'Updated' : isCopyMode ? 'Copied' : 'Added'} program: ${formData.displayTitle || formData.key}`, true);
      
      console.log("[AgeGroupVariationModal] Save successful to latest draft version");
      toast.success(`Program ${isEditMode ? 'updated' : isCopyMode ? 'copied' : 'created'} successfully`);

      // Dispatch an event to notify other components that a version has changed
      if (typeof window !== 'undefined') {
        console.log("[AgeGroupVariationModal] Dispatching locationVersionChanged event");
        const event = new CustomEvent("locationVersionChanged");
        window.dispatchEvent(event);
      }
      
      onSaved(updatedLocation);
      
      // Handle "Add another" functionality (only for add/copy modes, not edit)
      if (!isEditMode && addAnotherChecked) {
        // Save the current form data for reuse, including the key.
        const formDataToSave = { ...formData };
        console.log("[AgeGroupVariationModal] Saving all form data for 'Add another'", formDataToSave);
        setSavedFormData(formDataToSave);
        
        // Close modal
        onClose();
        
        // Dispatch event for parent to reopen modal with both form data and checkbox state
        setTimeout(() => {
          const addAnotherEvent = new CustomEvent('programAddAnother', {
            detail: { 
              formData: formDataToSave,
              addAnotherChecked: true  // Preserve checkbox state
            }
          });
          window.dispatchEvent(addAnotherEvent);
        }, 300);
      } else {
        // Normal close without reopening
        onClose();
      }
    } catch (error) {
      console.error("[AgeGroupVariationModal] Error saving program:", error);
      toast.error("Failed to save program");
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleFormDirtyChange = (dirty: boolean) => {
    setIsDirty(dirty);
  };
  
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    if (isOpen && !wasOpen) {
      setIsDirty(false);
      setMode("form");
      setJsonText("");
      setFormSnapshot(null);
      setFormRemountKey(k => k + 1);
    }
    if (!isOpen) {
      // Reset "Add another" state when modal closes
      setAddAnotherChecked(false);
      setSavedFormData(null);
    }
  }, [isOpen]);
  
  // This function ensures the form is properly submitted when saving
  const handleSaveClick = () => {
    console.log("[AgeGroupVariationModal] Save button clicked, submitting form");
    const form = document.getElementById("age-group-variation-form");
    if (form) {
      console.log("[AgeGroupVariationModal] Form found, submitting");
      form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    } else {
      console.error("[AgeGroupVariationModal] Form not found with ID 'age-group-variation-form'");
    }
  };
  
  // Determine the default values for the form with proper priority
  const getFormDefaultValues = () => {
    // Priority 1: Use savedFormDataForAddAnother if it's provided from parent (Add another functionality)
    if (savedFormDataForAddAnother) {
      console.log("[AgeGroupVariationModal] Using savedFormDataForAddAnother for form defaults:", savedFormDataForAddAnother);
      return savedFormDataForAddAnother;
    }
    
    // Priority 2: Use local savedFormData if available
    if (savedFormData) {
      console.log("[AgeGroupVariationModal] Using local savedFormData for form defaults:", savedFormData);
      return savedFormData;
    }
    
    // Priority 3: Copy mode - use variationToEdit but clear the key
    if (isCopyMode && variationToEdit) {
      console.log("[AgeGroupVariationModal] Using copy mode defaults (cleared key):", {...variationToEdit, key: ''});
      return {...variationToEdit, key: ''};
    }
    
    // Priority 4: Edit mode - use variationToEdit as-is
    if (variationToEdit) {
      console.log("[AgeGroupVariationModal] Using edit mode defaults:", variationToEdit);
      return variationToEdit;
    }
    
    // Priority 5: Default empty form
    console.log("[AgeGroupVariationModal] Using empty form defaults");
    return undefined;
  };

  const handleToggleJsonForm = () => {
    if (mode === "form") {
      const data = formSnapshot ?? getFormDefaultValues();
      const json = JSON.stringify(data ?? {}, null, 2) ?? "{}";
      setJsonText(json);
      setMode("json");
    } else {
      try {
        const parsed = JSON.parse(jsonText);
        setFormSnapshot(parsed);
        setFormRemountKey(k => k + 1);
        setMode("form");
      } catch {
        toast.error("Invalid JSON. Fix the syntax before switching to form.");
      }
    }
  };

  const formDefaultsForRender = mode === "form" && formSnapshot
    ? formSnapshot
    : getFormDefaultValues();

  const handleSaveFromModal = () => {
    if (mode === "json") {
      try {
        const parsed = JSON.parse(jsonText) as VariationFormData;
        const result = locationProgramVariationSchema.safeParse(parsed);
        if (!result.success) {
          toast.error("Invalid data: " + (result.error.errors[0]?.message ?? "Check all required fields"));
          return;
        }
        const stored = isEditMode
          ? location.variations?.find((v) => v.key === variationToEdit?.key)
          : undefined;
        const unapplied = unappliedJsonVariationKeys(parsed as Record<string, unknown>, stored as Record<string, unknown> | undefined);
        if (unapplied.length > 0) {
          const kept = unapplied.filter((k) => stored != null && Object.prototype.hasOwnProperty.call(stored, k));
          const notStored = unapplied.filter((k) => !kept.includes(k));
          const parts = [`Not saved: ${unapplied.join(", ")}. This form does not edit ${unapplied.length === 1 ? "that field" : "those fields"}.`];
          if (kept.length > 0) parts.push(`Stored value kept: ${kept.join(", ")}.`);
          if (kept.length > 0 && notStored.length > 0) parts.push(`No value saved: ${notStored.join(", ")}.`);
          toast.warning(parts.join(" "));
        }
        handleSave(result.data);
      } catch {
        toast.error("Invalid JSON. Fix the syntax before saving.");
      }
    } else {
      handleSaveClick();
    }
  };
  
  // One ownership badge for the whole variation (a keyed-array element `/variations/[key=X]`), not per
  // sub-field (§6). Only in edit mode with a real key; a copy/add has no record yet. A `g-` shared
  // program is owned by the org's `sharedVariations` record, which the location doc does not carry, so
  // its badge renders nothing here (correct — no wrong badge) until the org form surfaces it.
  const variationBadge =
    isEditMode && variationToEdit?.key ? (
      // Audience inherited from this modal's provider (below) — staff for an admin, director in welcome.
      <FieldOwnershipBadge fieldPath={encodeFieldPath(["variations", { key: variationToEdit.key }])} />
    ) : null;

  return <FieldStateProvider kind="campLocation" id={location?.id} audience={fieldAudience} currentUid={currentUser?.uid ?? null} enabled={isEditMode && !!variationToEdit?.key}>
    <BigFormModal
    isOpen={isOpen}
    onClose={onClose}
    title={modalTitle}
    isSaving={isSaving}
    isDirty={isDirty}
    description={<div className="space-y-2">
      {variationBadge}
      <p className="text-slate-700 font-bold">Programs describe your offerings. They include eligibility, timing, and pricing details.
For example, you might have a program for "Afternoon camp for ages 8-12."</p>
    </div>}
    onSave={handleSaveFromModal}
    showAddAnother={!isEditMode}
    addAnotherChecked={addAnotherChecked}
    onAddAnotherChange={setAddAnotherChecked}
    extraActions={
      isAdmin ? (
        <Button
          type="button"
          variant="outline"
          onClick={handleToggleJsonForm}
          disabled={isSaving}
        >
          {mode === "form" ? "Enter with JSON" : "Enter with form"}
        </Button>
      ) : undefined
    }
  >
    {mode === "form" ? (
      <AgeGroupVariationForm 
        key={`form-${formRemountKey}`}
        defaultValues={formDefaultsForRender} 
        onSubmit={handleSave} 
        onDirtyChange={handleFormDirtyChange} 
        onValuesChange={setFormSnapshot}
        existingKeys={existingKeys}
        isEditMode={isEditMode}
        schema={locationProgramVariationSchema}
      />
    ) : (
      <Textarea
        className="min-h-[400px] font-mono text-sm"
        placeholder='{"displayTitle":"Program name","key":"prog-1",...}'
        value={jsonText}
        onChange={(e) => {
          setJsonText(e.target.value);
          setIsDirty(true);
        }}
      />
    )}
  </BigFormModal>
  </FieldStateProvider>;
};
